import { TaJson } from "ta-json";

import CultureInfo from "../culture-info";
import Guard from "../guard";
import { PageResource } from "../models/pages/page-resource";
import { IExtendedContentHubClient } from "./extended-client";
import { CancelCallback } from "./internal-client";
import { ResponseHandler } from "./response-handler";

export interface IPagesClient {
    /**
     * Get a page resource by identifier.
     * @param identifier - The identifier of the page
     * @param culture - A culture to load the page in
     * @param entityId - The current entity id (Optional)
     * @param cancelCallback - A {@link CancelCallback} that will be placed in an Axios {@link CancelToken} if provided
     * @returns A promise resolving to a {@link PageResource}.
     * @throws An error when the resource can't be found.
     */
    getPageByIdentifierAsync(
        identifier: string,
        culture: CultureInfo,
        entityId?: number,
        cancelCallback?: CancelCallback
    ): Promise<PageResource>;

    /**
     * Get a page resource by name.
     * @param name - The name of the page
     * @param culture - A culture to load the page in
     * @param entityId - The current entity id (Optional)
     * @param cancelCallback - A {@link CancelCallback} that will be placed in an Axios {@link CancelToken} if provided
     * @returns A promise resolving to a {@link PageResource}.
     * @throws An error when the resource can't be found.
     */
    getPageByNameAsync(
        name: string,
        culture: CultureInfo,
        entityId?: number,
        cancelCallback?: CancelCallback
    ): Promise<PageResource>;

    /**
     * Get a page resource by id.
     * @param id - The id of the page
     * @param culture - A culture to load the page in
     * @param entityId - The current entity id (Optional)
     * @param cancelCallback - A {@link CancelCallback} that will be placed in an Axios {@link CancelToken} if provided
     * @returns A promise resolving to a {@link PageResource}.
     * @throws An error when the resource can't be found.
     */
    getPageByIdAsync(
        id: number,
        culture: CultureInfo,
        entityId?: number,
        cancelCallback?: CancelCallback
    ): Promise<PageResource>;
}

export class PagesClient implements IPagesClient {
    private readonly _client: IExtendedContentHubClient;

    constructor(client: IExtendedContentHubClient) {
        this._client = client;
    }

    async getPageByNameAsync(
        name: string,
        culture: CultureInfo,
        entityId?: number,
        cancelCallback?: CancelCallback
    ): Promise<PageResource> {
        Guard.stringNotNullOrEmpty(name);
        Guard.notNullOrUndefined(culture);

        return this.getPageByIdentifierAsync(name, culture, entityId, cancelCallback);
    }

    async getPageByIdentifierAsync(
        identifier: string,
        culture: CultureInfo,
        entityId?: number,
        cancelCallback?: CancelCallback
    ): Promise<PageResource> {
        Guard.stringNotNullOrEmpty(identifier);
        Guard.notNullOrUndefined(culture);

        const link = await this._client.linkHelper.pageResourceToLinkAsync(identifier, culture, entityId);
        const response = await this._client.raw.getAsync(link.href, undefined, cancelCallback);

        ResponseHandler.handleErrors(response);

        return TaJson.deserialize<PageResource>(response.content, PageResource);
    }

    async getPageByIdAsync(
        id: number,
        culture: CultureInfo,
        entityId?: number,
        cancelCallback?: CancelCallback
    ): Promise<PageResource> {
        Guard.validId(id);
        Guard.notNullOrUndefined(culture);

        const link = await this._client.linkHelper.pageResourceToLinkAsync(id, culture, entityId);
        const response = await this._client.raw.getAsync(link.href, undefined, cancelCallback);

        ResponseHandler.handleErrors(response);

        return TaJson.deserialize<PageResource>(response.content, PageResource);
    }
}
